import { isNativeIOS } from './platform';
import { api } from './api';
import { localDateKey } from './dateKey';
import { durationMinutesFromHealth, mapHealthKitWorkout } from './healthKitWorkoutMap';
import { PeakHealth, type PeakHealthWorkout } from './peakHealth';

const SYNC_THROTTLE_MS = 15 * 60 * 1000;
const LAST_SYNC_KEY = 'appleHealthLastSyncAt';
const AUTH_KEY = 'appleHealthAuthorized';
const API_TIMEOUT_MS = 12_000;
const NATIVE_TIMEOUT_MS = 30_000;
const SYNC_TIMEOUT_MS = 40_000;

export type HealthImportSummary = {
  steps: number | null;
  activeCalories: number | null;
  weightKg: number | null;
  importedWorkouts: number;
};

/** Only one HealthKit auth/sync at a time — concurrent requestAuthorization hangs iOS. */
let syncInFlight: Promise<HealthImportSummary> | null = null;

function shouldThrottleAutoSync(): boolean {
  try {
    const raw = localStorage.getItem(LAST_SYNC_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < SYNC_THROTTLE_MS;
  } catch {
    return false;
  }
}

function markSynced(): void {
  try {
    localStorage.setItem(LAST_SYNC_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function isAppleHealthAuthorized(): boolean {
  try {
    return localStorage.getItem(AUTH_KEY) === '1';
  } catch {
    return false;
  }
}

export function markAppleHealthAuthorized(): void {
  try {
    localStorage.setItem(AUTH_KEY, '1');
  } catch {
    /* ignore */
  }
}

function healthErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? '');
  const lower = raw.toLowerCase();
  if (lower.includes('timed out') || lower.includes('timeout')) {
    return 'Apple Health timed out. Tap Connect again and accept the Health permission sheet when it appears.';
  }
  if (lower.includes('not available')) {
    return 'Apple Health is not available here. Install the app on a real iPhone (HealthKit does not work in the Simulator).';
  }
  if (lower.includes('denied') || lower.includes('authorization') || lower.includes('not authorized')) {
    return 'Health access was denied. Enable Peak Performance IQ in Settings → Health → Data Access & Devices.';
  }
  if (raw.trim()) return raw;
  return 'Could not sync Apple Health.';
}

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Request HealthKit permissions — this is what makes the app appear in Health settings. */
export async function ensureAppleHealthAuth(): Promise<void> {
  if (!isNativeIOS()) throw new Error('Apple Health is only available on the iOS app.');

  const availability = await withTimeout(PeakHealth.isAvailable(), 8_000, 'Health availability check');
  if (!availability?.available) {
    throw new Error(
      'Apple Health is not available here. Install the app on a real iPhone (HealthKit does not work in the Simulator).',
    );
  }

  await withTimeout(PeakHealth.requestAuthorization(), NATIVE_TIMEOUT_MS, 'Health permission request');
  markAppleHealthAuthorized();
}

async function syncAppleHealthInner(options?: {
  workoutDays?: number;
  force?: boolean;
}): Promise<HealthImportSummary> {
  if (!isNativeIOS()) {
    throw new Error('Apple Health is only available on the iOS app.');
  }

  if (!options?.force && shouldThrottleAutoSync() && isAppleHealthAuthorized()) {
    const todayKey = localDateKey();
    const day = await withTimeout(
      api<{
        healthSteps?: number | null;
        healthActiveCalories?: number | null;
      }>(`/api/nutrition/day/${todayKey}`).catch(() => null),
      API_TIMEOUT_MS,
      'Load cached health day',
    ).catch(() => null);
    return {
      steps: day?.healthSteps ?? null,
      activeCalories: day?.healthActiveCalories ?? null,
      weightKg: null,
      importedWorkouts: 0,
    };
  }

  // Always request auth first so the system sheet appears and the app is listed in Health settings.
  await ensureAppleHealthAuth();

  const todayKey = localDateKey();
  const workoutDays = options?.workoutDays ?? 14;

  const summary = await withTimeout(PeakHealth.queryTodaySummary(), NATIVE_TIMEOUT_MS, 'Read today steps/calories');
  const steps = Math.max(0, Math.round(Number(summary.steps) || 0));
  const activeCalories = Math.max(0, Math.round(Number(summary.activeCalories) || 0));

  let weightKg: number | null = null;
  try {
    const weightRes = await withTimeout(PeakHealth.queryLatestWeight(), NATIVE_TIMEOUT_MS, 'Read weight');
    const w = weightRes.weightKg;
    weightKg = w != null && Number(w) > 0 ? Number(w) : null;
  } catch (e) {
    console.warn('[appleHealth] weight query failed', e);
  }

  if (weightKg != null) {
    await withTimeout(
      api('/api/body-check-ins', {
        method: 'POST',
        body: JSON.stringify({ weight: weightKg, weightUnit: 'kg', measuredAt: new Date().toISOString() }),
      }).catch(() => undefined),
      API_TIMEOUT_MS,
      'Save weight',
    ).catch(() => undefined);
  }

  try {
    await withTimeout(
      api(`/api/nutrition/day/${todayKey}/activity`, {
        method: 'PUT',
        body: JSON.stringify({
          steps,
          activeCalories,
          source: 'apple_health',
        }),
      }),
      API_TIMEOUT_MS,
      'Save activity',
    );
  } catch (e) {
    console.warn('[appleHealth] failed to save activity to API', e);
  }

  let importedWorkouts = 0;
  let workouts: PeakHealthWorkout[] = [];
  try {
    const res = await withTimeout(
      PeakHealth.queryRecentWorkouts({ days: workoutDays }),
      NATIVE_TIMEOUT_MS,
      'Read workouts',
    );
    workouts = Array.isArray(res.workouts) ? res.workouts : [];
  } catch (e) {
    console.warn('[appleHealth] workout query failed', e);
  }

  const importPayload = workouts
    .filter((w) => w.uuid && w.startDate)
    .map((w) => {
      const mapped = mapHealthKitWorkout(w.workoutActivityName, w.workoutActivityId);
      const durationMinutes = durationMinutesFromHealth(w.duration, w.startDate, w.endDate);
      const kcal =
        w.totalEnergyBurned != null && Number(w.totalEnergyBurned) > 0
          ? Math.round(Number(w.totalEnergyBurned))
          : undefined;
      const distanceM =
        w.totalDistance != null && Number(w.totalDistance) > 0 ? Number(w.totalDistance) : null;
      const notes =
        distanceM != null
          ? `Imported from Apple Health · ${(distanceM / 1000).toFixed(2)} km`
          : 'Imported from Apple Health';

      return {
        healthKitUuid: w.uuid,
        activityType: mapped.activityType,
        name: mapped.name,
        startedAt: w.startDate,
        durationMinutes,
        estimatedCaloriesBurned: kcal,
        notes,
        distanceMeters: distanceM ?? undefined,
      };
    });

  if (importPayload.length > 0) {
    try {
      const res = await withTimeout(
        api<{ importedCount: number }>('/api/workouts/import-apple-health', {
          method: 'POST',
          body: JSON.stringify({ workouts: importPayload }),
        }),
        API_TIMEOUT_MS,
        'Import workouts',
      );
      importedWorkouts = res.importedCount ?? 0;
    } catch (e) {
      console.warn('[appleHealth] workout import failed', e);
    }
  }

  markSynced();
  markAppleHealthAuthorized();

  return {
    steps,
    activeCalories,
    weightKg,
    importedWorkouts,
  };
}

/** Full sync via first-party PeakHealth plugin (auth sheet + statistics queries). */
export async function syncAppleHealth(options?: {
  workoutDays?: number;
  force?: boolean;
}): Promise<HealthImportSummary> {
  if (syncInFlight) {
    return syncInFlight;
  }

  syncInFlight = (async () => {
    try {
      return await withTimeout(syncAppleHealthInner(options), SYNC_TIMEOUT_MS, 'Apple Health sync');
    } catch (e) {
      throw new Error(healthErrorMessage(e));
    } finally {
      syncInFlight = null;
    }
  })();

  return syncInFlight;
}

/** @deprecated Use syncAppleHealth — kept for Profile button compatibility */
export async function importFromAppleHealth(): Promise<HealthImportSummary> {
  return syncAppleHealth({ force: true });
}

export function formatHealthSyncSuccess(summary: HealthImportSummary): string {
  const parts: string[] = [];
  if (summary.steps != null) parts.push(`${summary.steps.toLocaleString()} steps`);
  if (summary.activeCalories != null) parts.push(`${summary.activeCalories} active cal`);
  if (summary.weightKg != null) parts.push(`${summary.weightKg.toFixed(1)} kg`);
  if (summary.importedWorkouts > 0) {
    parts.push(
      `${summary.importedWorkouts} workout${summary.importedWorkouts === 1 ? '' : 's'} imported`,
    );
  }
  return parts.length ? `Synced · ${parts.join(' · ')}` : 'Synced with Apple Health (no new data today).';
}
